import { useCallback, useMemo, useState } from 'react';

import * as yup from 'yup';
import { useFormik } from 'formik';
import styled from '@emotion/styled';
import { ChildProtection, FormFooter } from 'components';
import { CustomerJourneyStep, MALAYSIA_COUNTRY_ID, useCustomerJourneyStep } from 'core';
import { ForWhom, RegistrationCandidateDetails as FormModel } from 'store/candidate/types';
import { isPanNumberCountry, selectSelectedCountryId } from 'store/country/countrySlice';
import { selectIsLoggedUser } from 'store/user/userSlice';
import { selectBasketExams } from 'store/basket/basketSlice';
import { useAppSelector } from 'store';
import { useGetChildCandidatesQuery, useLazyChildRequireMigrationQuery } from 'store/candidate/candidateAPI';
import { useGetCurrentSessionsQuery } from 'store/exams-search/examsSearchAPI';
import { useGetIsEiiEnabledQuery } from 'store/features/featuresAPI';
import { StartUserMigrationModal } from 'components/UserMigration/StartUserMigrationModal';

import { useFormFields, useServerErrors } from '@britishcouncil/react-forms';
import { Divider, MainContainer, PageContainer, Quote } from '@britishcouncil/react-solas-ors3';
import { Button, LoadingContainer } from '@britishcouncil/react-solas';

import { CandidateFields, ChooseTestTaker, ParentContactDetailsFields } from './components';
import { MALAYSIA_CITIZEN_VALIDATION_RULE, REQUIRED_NATIVE_LANGUAGE_RULE, VALIDATION_SCHEMA } from './validationSchema';
import { useCandidateDetailsSubmit } from './useCandidateDetailsSubmit';
import { useCandidateInitialValues } from './useCandidateInitialValues';
import { useGetTaxRateQuery } from 'store/organisation-country/organisation-country-API';

export const CandidateDetails = () => {
  useCustomerJourneyStep(CustomerJourneyStep.CandidateDetails);
  const selectedCountryId = useAppSelector(selectSelectedCountryId);
  const canSetPanNumber = useAppSelector(isPanNumberCountry);
  const isLoggedUser = useAppSelector(selectIsLoggedUser);
  const { data: isEiiEnabled, isFetching: eiiEnableLoading } = useGetIsEiiEnabledQuery(selectedCountryId, {
    skip: !isLoggedUser,
  });
  const { data: childCandidates, isFetching: childCandidatesLoading } = useGetChildCandidatesQuery(undefined, {
    skip: !isLoggedUser,
  });
  const { submit, serverErrors, isSubmitting } = useCandidateDetailsSubmit();
  const { data: currentSessions } = useGetCurrentSessionsQuery();
  const exams = useAppSelector(selectBasketExams);
  const { initialValues, onCandidateChange, isLoading: isCandidateLoading } = useCandidateInitialValues();
  const { data: taxRateData } = useGetTaxRateQuery({ countryId: selectedCountryId }, { skip: !isLoggedUser });

  const isRegistrationInCheckpointSession = useMemo(
    () =>
      exams.length > 0 &&
      currentSessions &&
      (currentSessions.find((s) => s.sessionId === exams[0].sessionId)?.checkpointOnly ?? false),
    [currentSessions, exams],
  );

  const isLoading = useMemo(
    () => eiiEnableLoading || childCandidatesLoading || isCandidateLoading,
    [eiiEnableLoading, childCandidatesLoading, isCandidateLoading],
  );

  const validationSchema = useMemo(
    () =>
      yup.object().shape({
        ...VALIDATION_SCHEMA.fields,
        ...(isRegistrationInCheckpointSession ? REQUIRED_NATIVE_LANGUAGE_RULE.fields : {}),
        ...(selectedCountryId === MALAYSIA_COUNTRY_ID.toString() && taxRateData && taxRateData.taxRate > 0
          ? MALAYSIA_CITIZEN_VALIDATION_RULE.fields
          : {}),
      }),
    [isRegistrationInCheckpointSession, selectedCountryId, taxRateData],
  );

  const formik = useFormik<FormModel>({
    enableReinitialize: true,
    initialValues: initialValues,
    validationSchema: validationSchema,
    onSubmit: submit,
  });
  const fields = useFormFields(formik);
  const mainErrors = useServerErrors(formik, serverErrors);

  const [showMigrationAccountModal, setShowMigrationAccountModal] = useState(false);

  const [triggerChildRequireMigrationQuery] = useLazyChildRequireMigrationQuery();

  let { shouldMigrateAccount, hasStartedMigration } = useMemo(
    () => ({
      shouldMigrateAccount: initialValues.isMinorToAdultMigrationAvailable,
      hasStartedMigration: initialValues.isMinorToAdultMigrationInProgress,
    }),
    [initialValues],
  );

  const closeMigrationModal = useCallback(
    (closeWithSuccess: boolean) => {
      if (closeWithSuccess) {
        onCandidateChange(formik.values.forWhom, formik.values.id);
        triggerChildRequireMigrationQuery();
      }
      setShowMigrationAccountModal(false);
    },
    [
      setShowMigrationAccountModal,
      onCandidateChange,
      triggerChildRequireMigrationQuery,
      formik.values.forWhom,
      formik.values.id,
    ],
  );

  return (
    <>
      <MainContainer>
        <h1>Candidate details</h1>
      </MainContainer>
      <LoadingContainer loading={isLoading}>
        <PageContainer className="page-container">
          <ChooseTestTaker
            forWhom={formik.values.forWhom}
            candidateId={formik.values.id}
            children={childCandidates?.children || []}
            maxChildCandidates={childCandidates?.maxChildCandidates}
            onChange={onCandidateChange}
          />
          {shouldMigrateAccount && !!formik.values.forWhom && (
            <DivCenteredItems>
              {!hasStartedMigration && (
                <Quote title="" intent="danger">
                  The candidate is over 18 years old. To create new registration - migration of account is required
                </Quote>
              )}
              {hasStartedMigration && (
                <Quote title="" intent="danger">
                  Process of migration minor to adult is in progress, new registration for this Candidate can't be
                  created until migration will be finished
                </Quote>
              )}
              {!hasStartedMigration && (
                <Button onClick={() => setShowMigrationAccountModal(true)}>Migrate Account</Button>
              )}
              <StartUserMigrationModal
                show={showMigrationAccountModal}
                onExit={closeMigrationModal}
                candidateId={formik.values.id as number}
              />
            </DivCenteredItems>
          )}
          {!shouldMigrateAccount && !!formik.values.forWhom && (
            <form onSubmit={formik.handleSubmit}>
              <CandidateFields fields={fields} showPanNumber={canSetPanNumber} showTaxNumber={isEiiEnabled} />
              {formik.values.forWhom !== ForWhom.Myself && (
                <>
                  <Divider />
                  <ParentContactDetailsFields fields={fields.guardianContactDetails} />
                  <ChildProtection
                    fields={fields.childDetails}
                    dateOfBirth={formik.values.dateOfBirth}
                    onGuardianAdd={(guardian) =>
                      formik.setFieldValue('childDetails.allowedPeople', [
                        ...formik.values.childDetails.allowedPeople,
                        guardian,
                      ])
                    }
                    onGuardianRemove={(index) => {
                      formik.setFieldValue(
                        'childDetails.allowedPeople',
                        formik.values.childDetails.allowedPeople.filter((x, i) => i !== index),
                      );
                    }}
                  />
                </>
              )}
              <FormFooter
                mainErrors={!isSubmitting ? mainErrors : undefined}
                submitCount={formik.submitCount}
                isValid={formik.isValid}
              >
                <Button intent="primary" type="submit" loading={isSubmitting} disabled={isSubmitting}>
                  Next
                </Button>
              </FormFooter>
            </form>
          )}
        </PageContainer>
      </LoadingContainer>
    </>
  );
};

const DivCenteredItems = styled.div`
  text-align: center;
`;
